/* global Word */

import Logger from "@/utility/logger";
import { buildGroundingReferenceTag, parseGroundingReferenceTag } from "@/utility/groundingMarkers";
import type { GroundingReferenceKind, GroundingReferenceTarget } from "@/types/grounding";

// Retrieves all bookmarks in the current Word document
export const getAllBookmarks = async (): Promise<string[]> => {
  return await Word.run(async (context) => {
    const body = context.document.body.getRange();
    let bookmarkNamesResult = body.getBookmarks(false, true);

    await context.sync();

    const bookmarkNames: string[] = bookmarkNamesResult.value;

    if (bookmarkNames.length === 0) {
      Logger.debug("No bookmarks found in the document.");
      return [];
    }

    return bookmarkNames;
  });
};

let lastWordRun: Promise<any> = Promise.resolve();

function queuedWordRun<T>(callback: (context: Word.RequestContext) => Promise<T>): Promise<T> {
  const run = () =>
    Word.run(async (context) => {
      try {
        return await callback(context);
      } catch (err) {
        Logger.error("Word.run error:", err);
        throw err;
      }
    });

  lastWordRun = lastWordRun.then(run, run);
  return lastWordRun;
}

export const hasSection = async (targetWord: string): Promise<boolean> => {
  return await queuedWordRun(async (context) => {
    const bookmarkRange = context.document.getBookmarkRangeOrNullObject(targetWord + "_START");
    bookmarkRange.load("isNullObject");
    await context.sync();

    return !bookmarkRange.isNullObject;
  });
};

export const hasSectionBookmarks = async (targetWord: string): Promise<boolean> => {
  return await queuedWordRun(async (context) => {
    const startBookmark = context.document.getBookmarkRangeOrNullObject(targetWord + "_START");
    const endBookmark = context.document.getBookmarkRangeOrNullObject(targetWord + "_END");

    startBookmark.load("isNullObject");
    endBookmark.load("isNullObject");
    await context.sync();

    return !startBookmark.isNullObject && !endBookmark.isNullObject;
  });
};

// Scrolls to a specific target word in the Word document
export const scrollToSection = async (targetWord: string) => {
  await Word.run(async (context) => {
    const bookmarkRange = context.document.getBookmarkRangeOrNullObject(targetWord + "_START");
    bookmarkRange.load("isNullObject");
    await context.sync();

    if (!bookmarkRange.isNullObject) {
      bookmarkRange.select();
      await context.sync();
    } else {
      Logger.warn("Bookmark not found:", targetWord);
    }
  });
};

export const scrollToGroundingReference = async (
  taskId: string,
  kind: GroundingReferenceKind,
  id: string,
  target?: Partial<GroundingReferenceTarget>
): Promise<boolean> => {
  return await queuedWordRun(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load("items/tag");
    await context.sync();

    const targetControl = contentControls.items.find((control) => {
      const parsedTag = parseGroundingReferenceTag(control.tag);
      if (!parsedTag || parsedTag.taskId !== taskId || parsedTag.expandSource !== false) return false;

      if (kind === "rationale") {
        return parsedTag.kind === "rationale" && parsedTag.id === id;
      }

      if (!parsedTag.sourceIds?.includes(id)) return false;

      if (target?.sourceOnly) {
        return parsedTag.sourceOnly === true;
      }

      if (target?.id && target.kind === "rationale") {
        return parsedTag.kind === "rationale" && parsedTag.id === target.id;
      }

      return true;
    });

    if (!targetControl) {
      const tag = buildGroundingReferenceTag(taskId, kind, id);
      const legacyContentControls = context.document.contentControls.getByTag(tag);
      legacyContentControls.load("items");
      await context.sync();

      const legacyTargetControl = legacyContentControls.items[0];
      if (!legacyTargetControl) {
        Logger.warn("wordBookmarks.ts: Grounding reference marker not found:", tag);
        return false;
      }

      legacyTargetControl.select();
      await context.sync();

      return true;
    }

    targetControl.select();
    await context.sync();

    return true;
  });
};
